import type { Band, Song, BandList, SongList } from '../models/types';
import { SongModel } from '../models/Song';

const IS_LOCAL = false;
const LIVE_BASE = 'https://api.liveshuffle.net';
const LOCAL_BASE = 'http://localhost:8888';
const API_VERSION_1 = 'api/v1';
const BANDS_ENDPOINT = 'bands';

export const getArtistsEndpoint = (): string => {
  if (IS_LOCAL) {
    return `${LOCAL_BASE}/${API_VERSION_1}/${BANDS_ENDPOINT}`;
  }
  return `${LIVE_BASE}/${API_VERSION_1}/${BANDS_ENDPOINT}`;
};

export const buildSongEndpoint = (
  collectionName: string,
  type?: string,
  customizedLoad?: string,
  source?: string
): string => {
  let url = LIVE_BASE;
  if (IS_LOCAL) {
    url = LOCAL_BASE;
  }
  url += `/${API_VERSION_1}/${collectionName}`;
  if (!type) type = 'random';
  if (!customizedLoad) customizedLoad = 'random';
  url += `/song/${type}/${customizedLoad}?source=${source || 'ls'}`;
  return url;
};

export const buildShowEndpoint = (
  collectionName: string,
  type?: string,
  customizedLoad?: string,
  source?: string
): string => {
  let url = LIVE_BASE;
  if (IS_LOCAL) {
    url = LOCAL_BASE;
  }
  url += `/${API_VERSION_1}/${collectionName}`;
  if (!type) type = 'random';
  if (!customizedLoad) customizedLoad = 'show';
  url += `/show/${type}/${customizedLoad}?source=${source || 'ls'}`;
  return url;
};

export class ApiService {
  static async fetchBands(): Promise<Band[]> {
    try {
      const endpoint = getArtistsEndpoint();
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const jsonData: BandList = await response.json();
      return jsonData.band_list.map((item: any) => ({
        bandName: item.band_name,
        bandIdx: item.band_idx,
        order: item.order,
        special: item.special,
        collection: item.collection,
        numShows: item.num_shows,
        numSongs: item.num_songs,
        source: item.source,
        years: item.years,
        monthDays: item.monthdays,
        yearMonths: item.yearmonths,
      }));
    } catch (error) {
      console.error('Error fetching bands:', error);
      return [];
    }
  }

  static async fetchSong(
    collection: string,
    type?: string,
    customPayload?: string,
    source?: string
  ): Promise<Song | null> {
    try {
      const endpoint = buildSongEndpoint(collection, type, customPayload, source);
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const jsonData = await response.json();
      return SongModel.fromJson(jsonData);
    } catch (error) {
      console.error('Error fetching song:', error);
      return null;
    }
  }

  static async fetchShow(
    collection: string,
    type?: string,
    customPayload?: string,
    source?: string
  ): Promise<Song[] | null> {
    try {
      const endpoint = buildShowEndpoint(collection, type, customPayload, source);
      const response = await fetch(endpoint);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const jsonData: SongList = await response.json();
      return jsonData.song_list.map(songJson => SongModel.fromJson(songJson));
    } catch (error) {
      console.error('Error fetching show:', error);
      return null;
    }
  }
}